import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

import VolumeUpOutlinedIcon from '@mui/icons-material/VolumeUpOutlined';
import Tooltip from '@mui/material/Tooltip';

import { getLinkLtik } from 'constants/LTIData';

import classes from './AudioIconProgress.module.css';

const url = window.location.pathname.split('/');

/**
 * AudioIconProgress Component
 *
 * Displays an audio icon with progress for a lesson or course item.
 * The icon is wrapped in a tooltip and links to the audio resource for the lesson.
 * The link adapts based on the user's access rights and the current URL context.
 *
 * Features:
 * - Dynamic link generation based on LTI and URL context
 * - Conditional rendering of the audio icon based on access rights
 * - Tooltip for accessibility and user guidance
 * - PropTypes and defaultProps for robust type checking
 *
 * @param {Object} props - Component props
 * @param {Object} props.data - Data object containing course and video IDs
 * @param {boolean} props.accessCourse - Whether the user has access to the course
 * @param {Object} [props.userTrack] - Optional user tracking data
 * @returns {JSX.Element} The rendered audio icon link
 */
const AudioIconProgress = props => {
  return (
    <Tooltip placement="bottom" title="Audio">
      <Link
        to={
          getLinkLtik()
            ? `/aud/${props.data.idCourse}/${
                props.data.idVideo
              }/?ltik=${getLinkLtik()}`
            : url.includes('a')
            ? `/a/aud/${props.data.idCourse}/${props.data.idVideo}`
            : url.includes('course')
            ? `/course/aud/${props.data.idCourse}/${props.data.idVideo}`
            : `/aud/${props.data.idCourse}/${props.data.idVideo}`
        }
        className={`${classes.productLesson} ${
          !props.accessCourse ? classes.productLessonDisabled : ''
        }`}
      >
        {/* Only render the audio icon if the user has access to the course */}
        {props.accessCourse && (
          <VolumeUpOutlinedIcon className={classes.audioIcon} />
          // Prop passed: className for icon styling
        )}
        <p className={classes.noDisplay}>.</p>
        {/* Prop passed: className for hidden paragraph styling */}
      </Link>
    </Tooltip>
  );
};

/**
 * PropTypes definition for type checking and development documentation
 * Provides runtime validation and serves as inline documentation
 */
AudioIconProgress.propTypes = {
  /**
   * Data object containing course and video IDs
   * Used to generate the audio link for the lesson
   * Passed as prop to Link for dynamic routing
   */
  data: PropTypes.shape({
    idCourse: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]),
    idVideo: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ])
  }),
  /**
   * Boolean indicating whether the user has access to the course
   * Controls icon rendering and link styling
   * Passed as prop to Link and icon
   */
  accessCourse: PropTypes.bool,
  /**
   * Optional user tracking data (not used directly in this component)
   */
  userTrack: PropTypes.object
};

AudioIconProgress.defaultProps = {
  data: { idCourse: '', idVideo: '' },
  accessCourse: false,
  userTrack: null
};

export default AudioIconProgress;
